import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusCircle, X, Loader2, AlertCircle, Trash2, Search, Globe, Users, Edit, Award, Building } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const TrainingSettings = () => {
  const [activeSection, setActiveSection] = useState('qualifications');
  const [countries, setCountries] = useState([]);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [companyQualifications, setCompanyQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Country management state
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [isEditingCountry, setIsEditingCountry] = useState(false);
  const [countryFormData, setCountryFormData] = useState({ id: null, name: '' });
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [showDeleteCountryConfirm, setShowDeleteCountryConfirm] = useState(false);
  
  // Site management state
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [isEditingSite, setIsEditingSite] = useState(false);
  const [siteFormData, setSiteFormData] = useState({ id: null, name: '', country: '' });
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [showDeleteSiteConfirm, setShowDeleteSiteConfirm] = useState(false);
  
  // Role management state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [roleFormData, setRoleFormData] = useState({ id: null, name: '', qualificationmatrix: [] });
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [showDeleteRoleConfirm, setShowDeleteRoleConfirm] = useState(false);
  
  // Qualification management state
  const [showQualificationModal, setShowQualificationModal] = useState(false);
  const [isEditingQualification, setIsEditingQualification] = useState(false);
  const [qualificationFormData, setQualificationFormData] = useState({ id: null, qualification_type: '', training_number: '', validity_time: '' });
  const [qualificationSearchTerm, setQualificationSearchTerm] = useState('');
  const [showDeleteQualificationConfirm, setShowDeleteQualificationConfirm] = useState(false);
  const [qualificationError, setQualificationError] = useState(null);
  const [trainingNumberError, setTrainingNumberError] = useState(false);
  const [qualificationFilter, setQualificationFilter] = useState('');
  const [showQualificationDropdown, setShowQualificationDropdown] = useState(false);

  // Listen for the triggerAdd event from the parent component
  useEffect(() => {
    const handleTriggerAdd = (event) => {
      if (event.detail.tab === 'settings') {
        if (activeSection === 'countries') {
          setIsEditingCountry(false);
          setShowCountryModal(true);
        } else if (activeSection === 'sites') {
          setIsEditingSite(false);
          setShowSiteModal(true);
        } else if (activeSection === 'roles') {
          setIsEditingRole(false);
          setShowRoleModal(true);
        } else if (activeSection === 'qualifications') {
          setIsEditingQualification(false);
          setShowQualificationModal(true);
          setShowQualificationDropdown(true);
        }
      }
    };

    window.addEventListener('triggerAdd', handleTriggerAdd);
    return () => {
      window.removeEventListener('triggerAdd', handleTriggerAdd);
    };
  }, [activeSection]);

  // Close qualification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showQualificationDropdown && !event.target.closest('.qualification-dropdown-container')) {
        setShowQualificationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQualificationDropdown]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [countriesResponse, sitesResponse, rolesResponse, qualificationTypesResponse, companyQualificationsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/training-countries/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          }),
          axios.get(`${API_BASE_URL}/training-sites/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          }),
          axios.get(`${API_BASE_URL}/training-roles/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          }),
          axios.get(`${API_BASE_URL}/qualificationtype/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          }),
          axios.get(`${API_BASE_URL}/company-qualifications/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          })
        ]);

        setCountries(countriesResponse.data);
        setSites(sitesResponse.data);
        setRoles(rolesResponse.data);
        setQualificationTypes(qualificationTypesResponse.data);
        setCompanyQualifications(companyQualificationsResponse.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch settings data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, []);

  // Country functions
  const handleCountryInputChange = (e) => {
    const { name, value } = e.target;
    setCountryFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetCountryForm = () => {
    setCountryFormData({ id: null, name: '' });
    setShowCountryModal(false);
    setIsEditingCountry(false);
    setShowDeleteCountryConfirm(false);
  };

  const handleCountrySubmit = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      };

      if (isEditingCountry) {
        const response = await axios.put(
          `${API_BASE_URL}/training-countries/${countryFormData.id}/`,
          { name: countryFormData.name },
          config
        );
        setCountries((prev) =>
          prev.map((country) => (country.id === response.data.id ? response.data : country))
        );
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/training-countries/`,
          { name: countryFormData.name },
          config
        );
        setCountries((prev) => [...prev, response.data]);
      }

      resetCountryForm();
    } catch (err) {
      setError(`Failed to ${isEditingCountry ? 'update' : 'add'} country`);
      console.error(err);
    }
  };

  const handleEditCountry = (country) => {
    setCountryFormData({ id: country.id, name: country.name });
    setIsEditingCountry(true);
    setShowCountryModal(true);
  };

  const handleDeleteCountry = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/training-countries/${countryFormData.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setCountries((prev) => prev.filter((country) => country.id !== countryFormData.id));
      resetCountryForm();
    } catch (err) {
      setError('Failed to delete country');
      console.error(err);
    }
  };

  // Site functions
  const handleSiteInputChange = (e) => {
    const { name, value } = e.target;
    setSiteFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetSiteForm = () => {
    setSiteFormData({ id: null, name: '', country: '' });
    setShowSiteModal(false);
    setIsEditingSite(false);
    setShowDeleteSiteConfirm(false);
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      };

      if (isEditingSite) {
        const response = await axios.put(
          `${API_BASE_URL}/training-sites/${siteFormData.id}/`,
          { name: siteFormData.name, country: siteFormData.country },
          config
        );
        setSites((prev) =>
          Array.isArray(prev) ? prev.map((site) => (site.id === response.data.id ? response.data : site)) : []
        );
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/training-sites/`,
          { name: siteFormData.name, country: siteFormData.country },
          config
        );
        setSites((prev) => [...prev, response.data]);
      }

      resetSiteForm();
    } catch (err) {
      setError(`Failed to ${isEditingSite ? 'update' : 'add'} site`);
      console.error(err);
    }
  };

  const handleEditSite = (site) => {
    setSiteFormData({ 
      id: site.id, 
      name: site.name, 
      country: site.country?.id || site.country_data?.id || '' 
    });
    setIsEditingSite(true);
    setShowSiteModal(true);
  };

  const handleDeleteSite = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/training-sites/${siteFormData.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setSites((prev) => Array.isArray(prev) ? prev.filter((site) => site.id !== siteFormData.id) : []);
      resetSiteForm();
    } catch (err) {
      setError('Failed to delete site');
      console.error(err);
    }
  };

  // Role functions
  const handleRoleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQualificationToggle = (qualificationId) => {
    setRoleFormData((prev) => ({
      ...prev,
      qualificationmatrix: prev.qualificationmatrix.includes(qualificationId)
        ? prev.qualificationmatrix.filter(id => id !== qualificationId)
        : [...prev.qualificationmatrix, qualificationId]
    }));
  };

  const resetRoleForm = () => {
    setRoleFormData({ id: null, name: '', qualificationmatrix: [] });
    setShowRoleModal(false);
    setIsEditingRole(false);
    setShowDeleteRoleConfirm(false);
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      };

      const dataToSend = {
        name: roleFormData.name,
        qualificationmatrix: roleFormData.qualificationmatrix
      };

      if (isEditingRole) {
        const response = await axios.put(
          `${API_BASE_URL}/training-roles/${roleFormData.id}/`,
          dataToSend,
          config
        );
        setRoles((prev) =>
          prev.map((role) => (role.id === response.data.id ? response.data : role))
        );
      } else {
        const response = await axios.post(`${API_BASE_URL}/training-roles/`, dataToSend, config);
        setRoles((prev) => [...prev, response.data]);
      }

      resetRoleForm();
    } catch (err) {
      let errorMessage = `Failed to ${isEditingRole ? 'update' : 'add'} role`;
      if (err.response?.data) {
        if (err.response.data.qualificationmatrix) {
          errorMessage = err.response.data.qualificationmatrix.join(', ');
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        }
      }
      setError(errorMessage);
      console.error(err);
    }
  };

  const handleEditRole = (role) => {
    // Map qualification IDs from role to company qualification types
    const roleQualificationIds = role.qualificationmatrix_data ? role.qualificationmatrix_data.map(q => q.id) : [];
    
    setRoleFormData({
      id: role.id,
      name: role.name,
      qualificationmatrix: roleQualificationIds
    });
    setIsEditingRole(true);
    setShowRoleModal(true);
  };

  const handleDeleteRole = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/training-roles/${roleFormData.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setRoles((prev) => prev.filter((role) => role.id !== roleFormData.id));
      resetRoleForm();
    } catch (err) {
      setError('Failed to delete role');
      console.error(err);
    }
  };

  // Qualification functions
  const handleQualificationInputChange = (e) => {
    const { name, value } = e.target;
    setQualificationFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing in training number field
    if (name === 'training_number' && trainingNumberError) {
      setTrainingNumberError(false);
      setQualificationError(null);
    }
  };

  const resetQualificationForm = () => {
    setQualificationFormData({ id: null, qualification_type: '', training_number: '', validity_time: '' });
    setShowQualificationModal(false);
    setIsEditingQualification(false);
    setShowDeleteQualificationConfirm(false);
    setQualificationError(null);
    setTrainingNumberError(false);
    setQualificationFilter('');
    setShowQualificationDropdown(false);
  };

  // Handle qualification search filter change
  const handleQualificationFilterChange = (value) => {
    setQualificationFilter(value);
    setShowQualificationDropdown(true);
  };

  // Handle qualification selection
  const handleQualificationSelect = (qualType) => {
    setQualificationFormData({ ...qualificationFormData, qualification_type: qualType.id });
    setQualificationFilter(qualType.name);
    setShowQualificationDropdown(false);
  };

  // Handle clear qualification
  const handleClearQualification = () => {
    setQualificationFormData({ ...qualificationFormData, qualification_type: '' });
    setQualificationFilter('');
  };

  // Filter qualifications based on search term
  const filteredQualificationTypes = qualificationTypes.filter(type => 
    type.name.toLowerCase().includes(qualificationFilter.toLowerCase())
  );

  const handleQualificationSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      };

      const dataToSend = {
        qualification_type: qualificationFormData.qualification_type,
        training_number: qualificationFormData.training_number,
        validity_time: parseInt(qualificationFormData.validity_time)
      };

      if (isEditingQualification) {
        const response = await axios.put(
          `${API_BASE_URL}/company-qualifications/${qualificationFormData.id}/`,
          dataToSend,
          config
        );
        setCompanyQualifications((prev) =>
          prev.map((qual) => (qual.id === response.data.id ? response.data : qual))
        );
      } else {
        const response = await axios.post(`${API_BASE_URL}/company-qualifications/`, dataToSend, config);
        setCompanyQualifications((prev) => [...prev, response.data]);
      }

      resetQualificationForm();
    } catch (err) {
      let errorMessage = `Failed to ${isEditingQualification ? 'update' : 'add'} qualification`;
      let isTrainingNumberError = false;
      
      if (err.response?.data) {
        if (err.response.data.training_number) {
          errorMessage = err.response.data.training_number[0];
          isTrainingNumberError = true;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        }
      }
      
      setQualificationError(errorMessage);
      setTrainingNumberError(isTrainingNumberError);
      console.error(err);
    }
  };

  const handleEditQualification = (qualification) => {
    setQualificationFormData({
      id: qualification.id,
      qualification_type: qualification.qualification_type,
      training_number: qualification.training_number,
      validity_time: qualification.validity_time.toString()
    });
    
    // Set the qualification filter to show the selected qualification name
    const selectedQualType = qualificationTypes.find(type => type.id === qualification.qualification_type);
    if (selectedQualType) {
      setQualificationFilter(selectedQualType.name);
    }
    
    setIsEditingQualification(true);
    setShowQualificationModal(true);
  };

  const handleDeleteQualification = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/company-qualifications/${qualificationFormData.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setCompanyQualifications((prev) => prev.filter((qual) => qual.id !== qualificationFormData.id));
      resetQualificationForm();
    } catch (err) {
      setError('Failed to delete qualification');
      console.error(err);
    }
  };

  // Filter functions
  const filteredCountries = Array.isArray(countries) ? countries.filter(country =>
    country.name.toLowerCase().includes(countrySearchTerm.toLowerCase())
  ) : [];

  const filteredSites = Array.isArray(sites) ? sites.filter(site => {
    const searchLower = siteSearchTerm.toLowerCase();
    return (
      site.name.toLowerCase().includes(searchLower) ||
      site.country_data?.name.toLowerCase().includes(searchLower)
    );
  }) : [];

  const filteredRoles = Array.isArray(roles) ? roles.filter(role =>
    role.name.toLowerCase().includes(roleSearchTerm.toLowerCase())
  ) : [];

  const filteredQualifications = Array.isArray(companyQualifications) ? companyQualifications.filter(qual => {
    const searchLower = qualificationSearchTerm.toLowerCase();
    return (
      qual.qualification_type_data?.name.toLowerCase().includes(searchLower) ||
      qual.training_number.toLowerCase().includes(searchLower) ||
      qual.validity_time.toString().includes(searchLower)
    );
  }) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-steel-50 rounded-xl shadow">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-brand-500 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Section Navigation */}
      <div className="bg-steel-50 border-b border-steel-200">
        <nav className="flex space-x-8 px-6" aria-label="Settings sections">
          <button
            onClick={() => setActiveSection('qualifications')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
              activeSection === 'qualifications'
                ? 'border-brand-500 text-brand-500'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
            }`}
          >
            <Award className="h-4 w-4 inline mr-2" />
            Qualifications
          </button>
          <button
            onClick={() => setActiveSection('roles')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
              activeSection === 'roles'
                ? 'border-brand-500 text-brand-500'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Roles
          </button>
          <button
            onClick={() => setActiveSection('sites')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
              activeSection === 'sites'
                ? 'border-brand-500 text-brand-500'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
            }`}
          >
            <Building className="h-4 w-4 inline mr-2" />
            Sites
          </button>
          <button
            onClick={() => setActiveSection('countries')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
              activeSection === 'countries'
                ? 'border-brand-500 text-brand-500'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
            }`}
          >
            <Globe className="h-4 w-4 inline mr-2" />
            Countries
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeSection === 'qualifications' ? (
          <div className="h-full flex flex-col">
            {/* Qualifications Search */}
            <div className="px-6 pt-4 pb-2">
              <div className="relative">
                <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search qualifications..."
                  value={qualificationSearchTerm}
                  onChange={(e) => setQualificationSearchTerm(e.target.value)}
                  className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Qualifications List */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 pb-6">
                {filteredQualifications.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium mb-2">No qualifications found</p>
                    <p className="text-slate-500 text-sm mb-4">
                      {qualificationSearchTerm ? 'Try adjusting your search terms.' : 'Add your first qualification to get started.'}
                    </p>
                    {!qualificationSearchTerm && (
                      <button
                        onClick={() => setShowQualificationModal(true)}
                        className="bg-brand-500 text-white px-4 py-2 rounded-md hover:bg-brand-600 transition-colors inline-flex items-center"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Add Qualification
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredQualifications.map((qualification) => (
                      <div
                        key={qualification.id}
                        className="bg-white p-4 rounded-xl border border-steel-200 hover:border-brand-300 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <Award className="h-5 w-5 text-brand-500 mr-3" />
                            <div className="flex items-center flex-1">
                              <span className="font-medium text-slate-900">{qualification.qualification_type_data?.name}</span>
                              <span className="text-slate-400 mx-2">•</span>
                              <span className="text-sm text-slate-600">Training #{qualification.training_number}</span>
                              <span className="text-slate-400 mx-2">•</span>
                              <span className="text-sm text-slate-600">{qualification.validity_time} days validity</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditQualification(qualification)}
                            className="text-brand-500 hover:text-brand-600 p-2 rounded-md hover:bg-brand-50 transition-colors ml-4"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeSection === 'roles' ? (
          <div className="h-full flex flex-col">
            {/* Roles Search */}
            <div className="px-6 pt-4 pb-2">
              <div className="relative">
                <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={roleSearchTerm}
                  onChange={(e) => setRoleSearchTerm(e.target.value)}
                  className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Roles List */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 pb-6">
                {filteredRoles.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium mb-2">No roles found</p>
                    <p className="text-slate-500 text-sm mb-4">
                      {roleSearchTerm ? 'Try adjusting your search terms.' : 'Add your first role to get started.'}
                    </p>
                    {!roleSearchTerm && (
                      <button
                        onClick={() => setShowRoleModal(true)}
                        className="bg-brand-500 text-white px-4 py-2 rounded-md hover:bg-brand-600 transition-colors inline-flex items-center"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Add Role
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredRoles.map((role) => (
                      <div
                        key={role.id}
                        className="bg-white p-6 rounded-xl border border-steel-200 hover:border-brand-300 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-3">
                              <Users className="h-5 w-5 text-brand-500 mr-3" />
                              <span className="font-medium text-slate-900 text-lg">{role.name}</span>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600 mb-2">Required Qualifications:</p>
                              {role.qualificationmatrix_data && role.qualificationmatrix_data.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {role.qualificationmatrix_data.map((companyQualification) => (
                                    <div
                                      key={companyQualification.id}
                                      className="inline-flex items-center px-2.5 py-1.5 rounded-xl text-xs border bg-brand-50 border-brand-200"
                                    >
                                      <span className="font-medium text-brand-800">
                                        {companyQualification.qualification_type_data?.name}
                                      </span>
                                      <span className="text-brand-500 text-xs ml-2">
                                        #{companyQualification.training_number}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-sm text-slate-500 italic">No qualifications required</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditRole(role)}
                            className="text-brand-500 hover:text-brand-600 p-2 rounded-md hover:bg-brand-50 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeSection === 'sites' ? (
          <div className="h-full flex flex-col">
            {/* Sites Search */}
            <div className="px-6 pt-4 pb-2">
              <div className="relative">
                <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search sites..."
                  value={siteSearchTerm}
                  onChange={(e) => setSiteSearchTerm(e.target.value)}
                  className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Sites List */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 pb-6">
                {filteredSites.length === 0 ? (
                  <div className="text-center py-12">
                    <Building className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium mb-2">No sites found</p>
                    <p className="text-slate-500 text-sm mb-4">
                      {siteSearchTerm ? 'Try adjusting your search terms.' : 'Add your first site to get started.'}
                    </p>
                    {!siteSearchTerm && (
                      <button
                        onClick={() => setShowSiteModal(true)}
                        className="bg-brand-500 text-white px-4 py-2 rounded-md hover:bg-brand-600 transition-colors inline-flex items-center"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Add Site
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSites.map((site) => (
                      <div
                        key={site.id}
                        className="bg-white p-4 rounded-xl border border-steel-200 hover:border-brand-300 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Building className="h-5 w-5 text-brand-500 mr-3" />
                            <div>
                              <span className="font-medium text-slate-900 block">{site.name}</span>
                              <span className="text-sm text-slate-500">{site.country_data?.name}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditSite(site)}
                            className="text-brand-500 hover:text-brand-600 p-1 rounded-md hover:bg-brand-50 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Countries Search */}
            <div className="px-6 pt-4 pb-2">
              <div className="relative">
                <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearchTerm}
                  onChange={(e) => setCountrySearchTerm(e.target.value)}
                  className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Countries List */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 pb-6">
                {filteredCountries.length === 0 ? (
                  <div className="text-center py-12">
                    <Globe className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium mb-2">No countries found</p>
                    <p className="text-slate-500 text-sm mb-4">
                      {countrySearchTerm ? 'Try adjusting your search terms.' : 'Add your first country to get started.'}
                    </p>
                    {!countrySearchTerm && (
                      <button
                        onClick={() => setShowCountryModal(true)}
                        className="bg-brand-500 text-white px-4 py-2 rounded-md hover:bg-brand-600 transition-colors inline-flex items-center"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Add Country
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCountries.map((country) => (
                      <div
                        key={country.id}
                        className="bg-white p-4 rounded-xl border border-steel-200 hover:border-brand-300 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Globe className="h-5 w-5 text-brand-500 mr-3" />
                            <span className="font-medium text-slate-900">{country.name}</span>
                          </div>
                          <button
                            onClick={() => handleEditCountry(country)}
                            className="text-brand-500 hover:text-brand-600 p-1 rounded-md hover:bg-brand-50 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Country Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-0 overflow-hidden">
            <div className="bg-brand-500 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {isEditingCountry ? 'Edit Country' : 'Add New Country'}
              </h3>
              <button
                onClick={resetCountryForm}
                className="text-white bg-brand-400 hover:bg-brand-600 rounded-full p-1 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCountrySubmit} className="p-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Country Name</label>
                <input
                  type="text"
                  name="name"
                  value={countryFormData.name}
                  onChange={handleCountryInputChange}
                  className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  placeholder="Enter country name"
                  required
                />
              </div>

              <div className="flex justify-between items-center mt-8 border-t pt-6">
                {isEditingCountry && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteCountryConfirm(true)}
                    className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center shadow-sm"
                  >
                    <Trash2 className="h-5 w-5 mr-2" />
                    Delete
                  </button>
                )}
                <div className="flex space-x-4 ml-auto">
                  <button
                    type="button"
                    onClick={resetCountryForm}
                    className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-brand-500 text-white px-5 py-2 rounded-xl hover:bg-brand-600 transition-colors font-medium shadow-sm"
                  >
                    {isEditingCountry ? 'Save Changes' : 'Add Country'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Site Modal */}
      {showSiteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-0 overflow-hidden">
            <div className="bg-brand-500 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {isEditingSite ? 'Edit Site' : 'Add New Site'}
              </h3>
              <button
                onClick={resetSiteForm}
                className="text-white bg-brand-400 hover:bg-brand-600 rounded-full p-1 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSiteSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Site Name</label>
                  <input
                    type="text"
                    name="name"
                    value={siteFormData.name}
                    onChange={handleSiteInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    placeholder="Enter site name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                  <select
                    name="country"
                    value={siteFormData.country}
                    onChange={handleSiteInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select a country</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-8 border-t pt-6">
                {isEditingSite && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteSiteConfirm(true)}
                    className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center shadow-sm"
                  >
                    <Trash2 className="h-5 w-5 mr-2" />
                    Delete
                  </button>
                )}
                <div className="flex space-x-4 ml-auto">
                  <button
                    type="button"
                    onClick={resetSiteForm}
                    className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-brand-500 text-white px-5 py-2 rounded-xl hover:bg-brand-600 transition-colors font-medium shadow-sm"
                  >
                    {isEditingSite ? 'Save Changes' : 'Add Site'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] p-0 overflow-hidden flex flex-col">
            <div className="bg-brand-500 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {isEditingRole ? 'Edit Role' : 'Add New Role'}
              </h3>
              <button
                onClick={resetRoleForm}
                className="text-white bg-brand-400 hover:bg-brand-600 rounded-full p-1 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleRoleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role Name</label>
                  <input
                    type="text"
                    name="name"
                    value={roleFormData.name}
                    onChange={handleRoleInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    placeholder="Enter role name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Required Qualifications</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-steel-200 rounded-xl p-3">
                    {companyQualifications.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-500 mb-2">No company qualifications available</p>
                        <p className="text-xs text-slate-400">Add qualifications in the Qualifications tab first</p>
                      </div>
                    ) : (
                      companyQualifications.map((companyQualification) => (
                        <label key={companyQualification.id} className="flex items-center space-x-3 cursor-pointer hover:bg-steel-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={roleFormData.qualificationmatrix.includes(companyQualification.id)}
                            onChange={() => handleQualificationToggle(companyQualification.id)}
                            className="rounded border-steel-300 text-brand-500 focus:ring-brand-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm text-slate-900">{companyQualification.qualification_type_data?.name}</span>
                            <div className="text-xs text-slate-500">
                              Training #{companyQualification.training_number} • {companyQualification.validity_time} days validity
                            </div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Select the company qualifications required for this role
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-center p-6 border-t bg-steel-50">
                {isEditingRole && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteRoleConfirm(true)}
                    className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center shadow-sm"
                  >
                    <Trash2 className="h-5 w-5 mr-2" />
                    Delete Role
                  </button>
                )}
                <div className="flex space-x-4 ml-auto">
                  <button
                    type="button"
                    onClick={resetRoleForm}
                    className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-brand-500 text-white px-5 py-2 rounded-xl hover:bg-brand-600 transition-colors font-medium shadow-sm"
                  >
                    {isEditingRole ? 'Save Changes' : 'Add Role'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Country Confirmation Modal */}
      {showDeleteCountryConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Confirm Deletion</h3>
            <p className="text-slate-600 text-center mb-6">
              Are you sure you want to delete the country <span className="font-semibold">"{countryFormData.name}"</span>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowDeleteCountryConfirm(false)}
                className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCountry}
                className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center"
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Site Confirmation Modal */}
      {showDeleteSiteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Confirm Deletion</h3>
            <p className="text-slate-600 text-center mb-6">
              Are you sure you want to delete the site <span className="font-semibold">"{siteFormData.name}"</span>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowDeleteSiteConfirm(false)}
                className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSite}
                className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center"
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Role Confirmation Modal */}
      {showDeleteRoleConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Confirm Deletion</h3>
            <p className="text-slate-600 text-center mb-6">
              Are you sure you want to delete the role <span className="font-semibold">"{roleFormData.name}"</span>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowDeleteRoleConfirm(false)}
                className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRole}
                className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center"
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Qualification Modal */}
      <Dialog open={showQualificationModal} onOpenChange={resetQualificationForm}>
        <DialogContent className="sm:max-w-[1100px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {isEditingQualification ? 'Edit Qualification' : 'Add New Qualification'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {qualificationError && (
              <div className="bg-red-50 border border-red-200 p-3 rounded mb-4">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                  <p className="text-red-700 text-sm">{qualificationError}</p>
                </div>
              </div>
            )}
            
            {/* Two columns layout */}
            <div className="grid grid-cols-2 gap-6 h-full">
              {/* Left column - Training Details */}
              <div className="space-y-4">
                <div className="border rounded p-4">
                  <h3 className="text-base font-medium text-slate-700 mb-3">Training Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Internal Training Number *</label>
                      <input
                        type="text"
                        name="training_number"
                        value={qualificationFormData.training_number}
                        onChange={handleQualificationInputChange}
                        className={`w-full mt-1 p-2 border rounded focus:outline-none focus:ring-1 focus:ring-brand-500 ${
                          trainingNumberError 
                            ? 'border-red-500' 
                            : 'border-steel-300'
                        }`}
                        placeholder="e.g., TR-001, SAFETY-101"
                        maxLength={20}
                        required
                      />
                      {trainingNumberError && (
                        <p className="text-red-500 text-xs mt-1">This training number already exists</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Validity Period *</label>
                      <div className="relative">
                        <input
                          type="number"
                          name="validity_time"
                          value={qualificationFormData.validity_time}
                          onChange={handleQualificationInputChange}
                          className="w-full mt-1 p-2 border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                          placeholder="365"
                          min="1"
                          required
                        />
                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm">days</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column - Qualification Type */}
              <div className="space-y-4 flex flex-col">
                <div className="border rounded p-4 flex-1 flex flex-col">
                  <h3 className="text-base font-medium text-slate-700 mb-3">Qualification Type</h3>
                  <div className="flex-1 flex flex-col">
                    <label className="text-sm font-medium text-slate-700 mb-2">Select Qualification *</label>
                    <div className="relative mb-2">
                      <input
                        type="text"
                        placeholder="Search for qualification types..."
                        value={qualificationFilter}
                        onChange={(e) => handleQualificationFilterChange(e.target.value)}
                        className="w-full p-2 pr-8 border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        required
                      />
                      <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                      {qualificationFormData.qualification_type && (
                        <button
                          type="button"
                          onClick={handleClearQualification}
                          className="absolute right-6 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 font-bold text-lg"
                          title="Clear selection"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    
                    {/* Always visible qualification list */}
                    <div className="flex-1 border border-steel-200 rounded bg-white" style={{minHeight: '300px', maxHeight: '300px', overflowY: 'auto'}}>
                      {(qualificationFilter === '' ? qualificationTypes : filteredQualificationTypes).length > 0 ? (
                        (qualificationFilter === '' ? qualificationTypes : filteredQualificationTypes).map((type) => (
                          <div
                            key={type.id}
                            onClick={() => handleQualificationSelect(type)}
                            className={`p-3 hover:bg-brand-50 cursor-pointer border-b border-steel-100 last:border-b-0 ${
                              qualificationFormData.qualification_type === type.id ? 'bg-brand-100 border-brand-300' : ''
                            }`}
                          >
                            <div className="text-sm font-medium text-slate-800">{type.name}</div>
                            {type.description && (
                              <div className="text-xs text-slate-500 mt-1">{type.description}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-slate-500 text-center">
                          No qualification types found
                        </div>
                      )}
                    </div>
                    
                    {(qualificationFilter === '' ? qualificationTypes : filteredQualificationTypes).length > 0 && (
                      <div className="text-xs text-slate-500 mt-1 text-center">
                        Showing {Math.min((qualificationFilter === '' ? qualificationTypes : filteredQualificationTypes).length, (qualificationFilter === '' ? qualificationTypes : filteredQualificationTypes).length)} of {(qualificationFilter === '' ? qualificationTypes : filteredQualificationTypes).length} results
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {isEditingQualification && (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteQualificationConfirm(true)}
                  className="mr-2"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="space-x-2">
              <Button variant="outline" onClick={resetQualificationForm}>
                Cancel
              </Button>
              <Button onClick={handleQualificationSubmit}>
                {isEditingQualification ? 'Save Changes' : 'Add Qualification'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Qualification Confirmation Modal */}
      {showDeleteQualificationConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Confirm Deletion</h3>
            <p className="text-slate-600 text-center mb-6">
              Are you sure you want to delete this qualification? This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowDeleteQualificationConfirm(false)}
                className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteQualification}
                className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center"
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingSettings;